import type { Alert } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  InfoButton,
  Label,
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import styles from "./SMSAlerts.module.scss"
import { usePolyglot } from "src/contexts"
import {
  useSliderInputProps,
  useGetGlucoseValue,
  useSetGlucoseValue,
  useGlucoseInputProps,
  useReadableGlucoseUnit,
} from "src/hooks"

const polyglotPrefix = "pages.alerts.sms.urgent_low_threshold."

const inputProps = {
  min: 0,
  max: 100,
}
const numberId = "urgent-low-sms-threshold"

interface Props {
  urgentLowSMS: Alert;
  updateAlert: (settings: Partial<Alert>) => void;
}

export function UrgentLowThreshold(props: Props) {
  const {
    urgentLowSMS,
    updateAlert,
  } = props

  const polyglot = usePolyglot()
  const readableGlucoseUnit = useReadableGlucoseUnit()

  const getGlucoseValue = useGetGlucoseValue()
  const setGlucoseValue = useSetGlucoseValue()
  const currentUrgentLow = urgentLowSMS.trigger_value

  const onValueChange = useCallback(
    (threshold: Alert["trigger_value"]) => {
      updateAlert({
        trigger_value: setGlucoseValue(threshold),
      })
    },
    [
      updateAlert,
      setGlucoseValue,
    ],
  )

  const sharedProps = useGlucoseInputProps(inputProps)

  const {
    inputValue,
    sliderValue,
    onSliderChange,
    onNumberChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    value: getGlucoseValue(currentUrgentLow),
    onChange: onValueChange,
    updatedAt: urgentLowSMS.updated_at,
    min: sharedProps.min,
    max: sharedProps.max,
    inputStep: sharedProps.step,
    sliderStep: sharedProps.step,
    toFixed: true,
  })

  return (
    <div>
      <Label>
        {polyglot.t(`${polyglotPrefix}label`)}
        <InfoButton title={polyglot.t(`${polyglotPrefix}info.title`)}>
          <div>
            {polyglot.t(`${polyglotPrefix}info.description_one`)}
          </div>
          <div>
            {polyglot.t(`${polyglotPrefix}info.description_two`)}
          </div>
        </InfoButton>
      </Label>
      <div className={styles.inputContainer}>
        <SliderValue
          sizePreset="small"
          alignmentPreset="center"
        >
          <SliderValueInput
            {...sharedProps}
            placeholder=""
            value={inputValue}
            onChange={onNumberChange}
            onBlur={onNumberBlur}
            onKeyDown={onNumberKeyDown}
            id={numberId}
          />
          <SliderValueLabelContainer>
            <SliderValueLabel htmlFor={numberId}>
              {readableGlucoseUnit}
            </SliderValueLabel>
          </SliderValueLabelContainer>
        </SliderValue>
        <Slider
          className={styles.inputSlider}
          id="urgent-low-sms-threshold-container"
        >
          <SliderInput
            {...sharedProps}
            value={sliderValue}
            onChange={onSliderChange}
            id="urgent-low-sms-threshold-slider"
          />
          <SliderTracks
            min={sharedProps.min}
            max={sharedProps.max}
          />
        </Slider>
      </div>
    </div>
  )
}
